import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Chip,
} from '@mui/material';
import {
  Science,
  Settings,
  CheckCircle,
  Calculate,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { experimentService } from '../services/experimentService';

interface PowerAnalysisResult {
  requiredSampleSizePerGroup: number;
  expectedPower: number;
  effectSize: number;
  alpha: number;
  beta: number;
  recommendation: string;
}

interface ValidationError {
  status: string;
  code: string;
  message: string;
  errors: Record<string, string>;
  timestamp: number;
}

const steps = ['Define Hypothesis', 'Power Analysis', 'Configure Experiment', 'Review & Launch'];

const ExperimentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Step 1: Hypothesis
  const [hypothesis, setHypothesis] = useState('');
  const [experimentName, setExperimentName] = useState('');
  const [experimentType, setExperimentType] = useState<string>('SEO_OPTIMIZATION');

  // Step 2: Power Analysis
  const [effectSize, setEffectSize] = useState<number>(0.5);
  const [alpha, setAlpha] = useState<number>(0.05);
  const [power, setPower] = useState<number>(0.80);
  const [powerResult, setPowerResult] = useState<PowerAnalysisResult | null>(null);
  const [calculatingPower, setCalculatingPower] = useState(false);

  // Step 3: Configuration - NEW FORMAT
  const [engineScope, setEngineScope] = useState<string[]>(['GOOGLE']);
  const [treatmentPages, setTreatmentPages] = useState<string>('');
  const [controlPages, setControlPages] = useState<string>('');
  const [duration, setDuration] = useState<number>(14);
  const [preWindowDays, setPreWindowDays] = useState<number>(28);
  const [postWindowDays, setPostWindowDays] = useState<number>(28);

  const handleNext = () => {
    if (activeStep === 0 && (!experimentName || !hypothesis)) {
      setError('Please provide experiment name and hypothesis');
      return;
    }
    if (activeStep === 2) {
      // Validate Step 3 configuration
      if (engineScope.length === 0) {
        setError('Please select at least one search engine');
        return;
      }
      
      // Parse and validate treatment pages
      const treatmentPageIds = treatmentPages
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
      
      if (treatmentPageIds.length === 0) {
        setError('Please provide at least one valid treatment page ID (numeric values only)');
        return;
      }

      // Parse control pages
      const controlPageIds = controlPages
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      // Check for mutual exclusivity - no page should be in both groups
      if (controlPageIds.length > 0) {
        const duplicates = treatmentPageIds.filter(id => controlPageIds.includes(id));
        if (duplicates.length > 0) {
          setError(`Page IDs must be mutually exclusive. Found page ID(s) in both groups: ${duplicates.join(', ')}`);
          return;
        }
      }
    }
    setError(null);
    setValidationErrors({});
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setValidationErrors({});
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleEngineChange = (engine: string) => {
    setEngineScope(prev => 
      prev.includes(engine) 
        ? prev.filter(e => e !== engine)
        : [...prev, engine]
    );
  };

  const calculatePowerAnalysis = async () => {
    setCalculatingPower(true);
    setError(null);

    try {
      const result = await experimentService.calculatePowerAnalysis({
        expectedEffectSize: effectSize,
        alpha: alpha,
        power: power,
      });
      setPowerResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to calculate power analysis');
    } finally {
      setCalculatingPower(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const treatmentPageIds = treatmentPages
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      const controlPageIds = controlPages
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      // Final validation: ensure mutual exclusivity
      if (controlPageIds.length > 0) {
        const duplicates = treatmentPageIds.filter(id => controlPageIds.includes(id));
        if (duplicates.length > 0) {
          setError(`Page IDs must be mutually exclusive. Found page ID(s) in both groups: ${duplicates.join(', ')}`);
          setLoading(false);
          return;
        }
      }

      const requestBody = {
        name: experimentName,
        type: experimentType,
        hypothesis: hypothesis,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + duration * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        engineScope: engineScope,
        treatmentPages: treatmentPageIds,
        ...(controlPageIds.length > 0 && { controlPages: controlPageIds }),
        preWindowDays: preWindowDays,
        postWindowDays: postWindowDays,
      };

      const experiment = await experimentService.createExperiment(requestBody);
      navigate(`/experiments/${experiment.id}`);
    } catch (err: any) {
      // Handle new error format
      if (err.response?.data?.errors) {
        const errorData = err.response.data as ValidationError;
        setValidationErrors(errorData.errors);
        setError(errorData.message || 'Validation failed');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create experiment');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Science sx={{ mr: 1, verticalAlign: 'middle' }} />
              Define Your Experiment
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Start by naming your experiment and stating your hypothesis.
            </Typography>

            <TextField
              fullWidth
              label="Experiment Name"
              value={experimentName}
              onChange={(e) => setExperimentName(e.target.value)}
              placeholder="e.g., FAQ Schema Impact Test Q1 2026"
              margin="normal"
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Experiment Type</InputLabel>
              <Select
                value={experimentType}
                onChange={(e) => setExperimentType(e.target.value)}
                label="Experiment Type"
                error={!!validationErrors.type}
              >
                <MenuItem value="SEO_OPTIMIZATION">SEO Optimization</MenuItem>
                <MenuItem value="CONTENT_CHANGE">Content Change</MenuItem>
                <MenuItem value="TECHNICAL_CHANGE">Technical Change</MenuItem>
                <MenuItem value="UI_UX_CHANGE">UI/UX Change</MenuItem>
              </Select>
              {validationErrors.type && (
                <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                  {validationErrors.type}
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Hypothesis"
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="e.g., Adding FAQ schema will increase CTR by 20%"
              margin="normal"
              multiline
              rows={3}
              required
              error={!!validationErrors.hypothesis}
              helperText={validationErrors.hypothesis || 'What do you expect to happen?'}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Calculate sx={{ mr: 1, verticalAlign: 'middle' }} />
              Power Analysis Calculator
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Determine the required sample size for statistically valid results.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Statistical Parameters
                    </Typography>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Expected Effect Size</InputLabel>
                      <Select
                        value={effectSize}
                        onChange={(e: SelectChangeEvent<number>) => setEffectSize(Number(e.target.value))}
                        label="Expected Effect Size"
                      >
                        <MenuItem value={0.2}>Small (0.2) - Subtle changes</MenuItem>
                        <MenuItem value={0.5}>Medium (0.5) - Typical SEO improvements</MenuItem>
                        <MenuItem value={0.8}>Large (0.8) - Major changes</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Significance Level (α)"
                      type="number"
                      value={alpha}
                      onChange={(e) => setAlpha(Number(e.target.value))}
                      inputProps={{ min: 0.001, max: 0.1, step: 0.01 }}
                      margin="normal"
                      helperText="Typically 0.05 (5% chance of false positive)"
                    />

                    <TextField
                      fullWidth
                      label="Statistical Power"
                      type="number"
                      value={power}
                      onChange={(e) => setPower(Number(e.target.value))}
                      inputProps={{ min: 0.5, max: 0.99, step: 0.05 }}
                      margin="normal"
                      helperText="Typically 0.80 (80% chance of detecting real effect)"
                    />

                    <Button
                      variant="contained"
                      onClick={calculatePowerAnalysis}
                      disabled={calculatingPower}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {calculatingPower ? <CircularProgress size={24} /> : 'Calculate Sample Size'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                {powerResult && (
                  <Card variant="outlined" sx={{ bgcolor: 'primary.50', borderColor: 'primary.main' }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom color="primary">
                        <CheckCircle sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                        Sample Size Recommendation
                      </Typography>

                      <Box sx={{ my: 2 }}>
                        <Typography variant="h3" color="primary.main">
                          {powerResult.requiredSampleSizePerGroup}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          URLs required per group
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="body2" paragraph>
                        {powerResult.recommendation}
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Total URLs Needed
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {powerResult.requiredSampleSizePerGroup * 2}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Expected Power
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {(powerResult.expectedPower * 100).toFixed(0)}%
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
              Configure Experiment
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Select search engines and define page groups for your experiment.
            </Typography>

            <Grid container spacing={3}>
              {/* Search Engine Scope */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: validationErrors.engineScope ? 'error.50' : 'background.paper' }}>
                  <CardContent>
                    <FormLabel component="legend" required error={!!validationErrors.engineScope}>
                      Search Engines
                    </FormLabel>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                      Select which search engines to track for this experiment
                    </Typography>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={engineScope.includes('GOOGLE')} 
                            onChange={() => handleEngineChange('GOOGLE')}
                          />
                        }
                        label="Google Search Console"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={engineScope.includes('BING')} 
                            onChange={() => handleEngineChange('BING')}
                          />
                        }
                        label="Bing Webmaster Tools"
                      />
                    </FormGroup>
                    {validationErrors.engineScope && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {validationErrors.engineScope}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Treatment Pages */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Treatment Page IDs *"
                  value={treatmentPages}
                  onChange={(e) => setTreatmentPages(e.target.value)}
                  placeholder="Enter one page ID per line&#10;1&#10;2&#10;3"
                  multiline
                  rows={8}
                  required
                  error={!!validationErrors.treatmentPages}
                  helperText={
                    validationErrors.treatmentPages || 
                    (() => {
                      const validIds = treatmentPages
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map(id => parseInt(id, 10))
                        .filter(id => !isNaN(id));
                      const totalLines = treatmentPages.split('\n').filter(u => u.trim()).length;
                      const invalidCount = totalLines - validIds.length;
                      return `${validIds.length} valid page ID(s)${invalidCount > 0 ? ` (${invalidCount} invalid)` : ''}${powerResult ? ` - need ${powerResult.requiredSampleSizePerGroup}` : ''}`;
                    })()
                  }
                />
              </Grid>

              {/* Control Pages */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Control Page IDs (Optional)"
                  value={controlPages}
                  onChange={(e) => setControlPages(e.target.value)}
                  placeholder="Enter one page ID per line&#10;4&#10;5&#10;6"
                  multiline
                  rows={8}
                  error={!!validationErrors.controlPages}
                  helperText={
                    validationErrors.controlPages ||
                    (() => {
                      if (!controlPages.trim()) return 'Optional - leave empty for no control group';
                      const validIds = controlPages
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map(id => parseInt(id, 10))
                        .filter(id => !isNaN(id));
                      const totalLines = controlPages.split('\n').filter(u => u.trim()).length;
                      const invalidCount = totalLines - validIds.length;
                      return `${validIds.length} valid page ID(s)${invalidCount > 0 ? ` (${invalidCount} invalid)` : ''}`;
                    })()
                  }
                />
              </Grid>

              {/* Duration */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Duration (Days)"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  inputProps={{ min: 7, max: 90 }}
                  helperText="Minimum 14 days recommended"
                />
              </Grid>

              {/* Pre-window Days */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Pre-Window (Days)"
                  value={preWindowDays}
                  onChange={(e) => setPreWindowDays(Number(e.target.value))}
                  inputProps={{ min: 7, max: 90 }}
                  helperText="Baseline period before experiment"
                />
              </Grid>

              {/* Post-window Days */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Post-Window (Days)"
                  value={postWindowDays}
                  onChange={(e) => setPostWindowDays(Number(e.target.value))}
                  inputProps={{ min: 7, max: 90 }}
                  helperText="Analysis period after experiment"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
              Review & Launch
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Review your experiment configuration before launching.
            </Typography>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Experiment Details</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Name</Typography>
                    <Typography variant="body1">{experimentName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Type</Typography>
                    <Typography variant="body1">{experimentType.replace(/_/g, ' ')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Hypothesis</Typography>
                    <Typography variant="body1">{hypothesis}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Search Engines</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {engineScope.map(engine => (
                        <Chip key={engine} label={engine} size="small" sx={{ mr: 1 }} />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Time Configuration</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Duration</Typography>
                    <Typography variant="body1">{duration} days</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Pre-Window</Typography>
                    <Typography variant="body1">{preWindowDays} days</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Post-Window</Typography>
                    <Typography variant="body1">{postWindowDays} days</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="textSecondary">Effect Size</Typography>
                    <Typography variant="body1">{effectSize}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Page Groups</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Treatment Pages</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {treatmentPages.split('\n').filter(u => u.trim()).length} pages
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Control Pages</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {controlPages.split('\n').filter(u => u.trim()).length} pages
                      {controlPages.split('\n').filter(u => u.trim()).length === 0 && ' (optional)'}
                    </Typography>
                  </Grid>
                </Grid>
                {powerResult && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Recommended: {powerResult.requiredSampleSizePerGroup} pages per group for {(power * 100).toFixed(0)}% power
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Create New Experiment
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Set up a controlled SEO experiment with statistical rigor
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => {
            setError(null);
            setValidationErrors({});
          }}>
            {error}
            {Object.keys(validationErrors).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block" fontWeight="bold">
                  Validation Errors:
                </Typography>
                {Object.entries(validationErrors).map(([field, message]) => (
                  <Typography key={field} variant="caption" display="block">
                    • {field}: {message}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            <Button
              onClick={() => navigate('/experiments')}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Launch Experiment'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExperimentCreate;
